import { t } from "i18next";
import { useState, useMemo } from "react";
import { validateArrayInput, sanitizeTextInput } from "utils/security";

function Tag({ isSelected, tag, onClick, isDisabled }) {
  const sanitizedTag = sanitizeTextInput(tag);
  
  return (
    <div
      className={`rounded-full py-1.5 px-3 capitalize font-medium transition-all duration-300 w-fit text-sm ${
        isSelected 
          ? "text-white bg-blue-600 shadow-sm" 
          : isDisabled
          ? "bg-gray-50 text-gray-400 cursor-not-allowed"
          : "bg-gray-200 text-gray-700 hover:bg-gray-200 cursor-pointer"
      }`}
      onClick={!isDisabled ? onClick : undefined}
    >
      {sanitizedTag}
    </div>
  );
}

export default function SecondStep({
  tags = [],
  formState,
  setFormState,
}) {
  const [showMoreTags, setShowMoreTags] = useState(false);
  
  // Randomize tags order and sanitize
  const shuffledTags = useMemo(() => {
    const sanitizedTags = tags.map(tag => sanitizeTextInput(tag)).filter(tag => tag.length > 0);
    return [...sanitizedTags].sort(() => Math.random() - 0.5);
  }, [tags]);

  return (
    <div className="w-full">

      {/* Form Content */}
      <div className="space-y-2">
        
        {/* Tags Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-gray-900">{t("signupTagsExplanation")}</h2>
          </div>

          <div className="flex gap-1.5 flex-wrap">
            {shuffledTags.slice(0, showMoreTags ? shuffledTags.length : 10).map((tag, index) => {
              const isSelected = formState.data.tags.includes(tag);
              const currentTags = formState.data.tags;
              const isDisabled = !isSelected && currentTags.length >= 6;
              
              return (
                <Tag
                  tag={tag}
                  key={index}
                  onClick={() => {
                    const sanitizedTag = sanitizeTextInput(tag);
                    if (sanitizedTag.length === 0) return;
                    
                    if (isSelected) {
                      // Always allow deselecting any tag
                      setFormState((pre) => ({
                        ...pre,
                        data: {
                          ...pre.data,
                          tags: pre.data.tags.filter((t) => t !== sanitizedTag),
                        },
                      }));
                    } else {
                      // Allow selecting if less than 6 tags are selected
                      if (currentTags.length < 6) {
                        const newTags = [...currentTags, sanitizedTag];
                        const validation = validateArrayInput(newTags, 6);
                        if (validation.isValid) {
                          setFormState((pre) => ({
                            ...pre,
                            data: {
                              ...pre.data,
                              tags: validation.sanitized,
                            },
                          }));
                        }
                      }
                    }
                  }}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                />
              );
            })}
          </div>

          {/* Show More/Less Button */}
          {shuffledTags.length > 10 && (
            <div className="flex justify-center w-full mt-2">
              <button
                onClick={() => setShowMoreTags((pre) => !pre)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200 text-xs font-medium"
              >
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-xs">
                    {showMoreTags ? "-" : "+"}
                  </span>
                </div>
                {showMoreTags ? t("signupLess") : t("signupMore")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
